/**
 * SelfOrder Vuex Module
 * セルフオーダーからの通知を管理する
 */
import { cable } from '@/plugins/websocket-client'

// 通知音の定義
const NOTIFICATION_SOUNDS = {
  newOrder: '/sounds/new-order.mp3',
  checkRequest: '/sounds/check-request.mp3',
}

export default {
  namespaced: true,

  state: {
    // ActionCable接続状態
    connected: false,
    subscription: null,

    // 通知関連
    unreadOrderCount: 0,
    notifications: [],

    // 通知設定
    soundEnabled: true,
    vibrationEnabled: true,

    // AudioPlayer連携
    audioPlayer: null,

    // 最新の通知データ
    latestOrder: null,
    latestCheckRequest: null,

    // 卓データ更新通知
    latestTableUpdate: null,
  },

  getters: {
    hasUnreadOrders: state => state.unreadOrderCount > 0,
    isConnected: state => state.connected,
    isSoundEnabled: state => state.soundEnabled,
  },

  mutations: {
    SET_CONNECTED(state, value) {
      state.connected = value
    },

    SET_SUBSCRIPTION(state, subscription) {
      state.subscription = subscription
    },

    INCREMENT_UNREAD_COUNT(state) {
      state.unreadOrderCount += 1
    },

    CLEAR_UNREAD_COUNT(state) {
      state.unreadOrderCount = 0
    },

    ADD_NOTIFICATION(state, notification) {
      state.notifications.unshift({
        ...notification,
        id: Date.now(),
        readAt: null,
      })
      // 最大100件まで保持
      if (state.notifications.length > 100) {
        state.notifications.pop()
      }
    },

    SET_LATEST_ORDER(state, order) {
      state.latestOrder = order
    },

    SET_LATEST_CHECK_REQUEST(state, request) {
      state.latestCheckRequest = request
    },

    SET_LATEST_TABLE_UPDATE(state, update) {
      state.latestTableUpdate = update
    },

    SET_SOUND_ENABLED(state, value) {
      state.soundEnabled = value
      localStorage.setItem('selfOrder.soundEnabled', JSON.stringify(value))
    },

    SET_VIBRATION_ENABLED(state, value) {
      state.vibrationEnabled = value
      localStorage.setItem('selfOrder.vibrationEnabled', JSON.stringify(value))
    },

    INIT_SETTINGS(state) {
      const soundEnabled = localStorage.getItem('selfOrder.soundEnabled')
      const vibrationEnabled = localStorage.getItem('selfOrder.vibrationEnabled')

      if (soundEnabled !== null) {
        state.soundEnabled = JSON.parse(soundEnabled)
      }
      if (vibrationEnabled !== null) {
        state.vibrationEnabled = JSON.parse(vibrationEnabled)
      }
    },

    SET_AUDIO_PLAYER(state, audioPlayer) {
      state.audioPlayer = audioPlayer
    },
  },

  actions: {
    /**
     * SelfOrderChannelに接続
     * チャンネル購読設定がある場合のみ接続する
     */
    connect({ commit, dispatch, state }) {
      if (state.subscription) {
        return // 既に接続済み
      }

      // チャンネル購読設定をチェック
      const channelSubscriptions = JSON.parse(localStorage.getItem('channelSubscriptions') || '[]')
      const hasSelfOrderSubscription = channelSubscriptions.some(
        sub => sub.channelName === 'self_order' || sub.channel_name === 'self_order',
      )

      if (!hasSelfOrderSubscription) {
        console.log('[SelfOrderChannel] No subscription configured, skipping connection')

        return // 購読設定がない場合は接続しない
      }

      // 設定を初期化
      commit('INIT_SETTINGS')

      const subscription = cable.subscriptions.create(
        { channel: 'SelfOrderChannel' },
        {
          connected() {
            commit('SET_CONNECTED', true)
            console.log('[SelfOrderChannel] Connected')
          },

          disconnected() {
            commit('SET_CONNECTED', false)
            console.log('[SelfOrderChannel] Disconnected')
          },

          received(data) {
            dispatch('handleMessage', data)
          },
        },
      )

      commit('SET_SUBSCRIPTION', subscription)
    },

    /**
     * 接続を切断
     */
    disconnect({ commit, state }) {
      if (state.subscription) {
        state.subscription.unsubscribe()
        commit('SET_SUBSCRIPTION', null)
        commit('SET_CONNECTED', false)
      }
    },

    /**
     * 受信したメッセージを処理
     */
    handleMessage({ commit, dispatch, state }, data) {
      console.log('[SelfOrderChannel] Received:', data)

      switch (data.type) {
        case 'new_order':
          commit('SET_LATEST_ORDER', data)
          commit('INCREMENT_UNREAD_COUNT')
          commit('ADD_NOTIFICATION', {
            type: 'new_order',
            table: data.table,
            items: data.items,
            totalAmount: data.total_amount,
            orderedAt: data.ordered_at,
          })
          dispatch('playNotificationSound', 'newOrder')
          dispatch('triggerVibration')
          break

        case 'check_request':
          commit('SET_LATEST_CHECK_REQUEST', data)
          commit('ADD_NOTIFICATION', {
            type: 'check_request',
            table: data.table,
            requestedAt: data.requested_at,
          })
          dispatch('playNotificationSound', 'checkRequest')
          dispatch('triggerVibration')
          break

        case 'status_change':
          // ステータス変更は通知不要
          break

        case 'table_data_updated':
          // 卓データ更新通知（画面自動更新用）
          commit('SET_LATEST_TABLE_UPDATE', {
            tableId: data.table.id,
            tableName: data.table.name,
            updateType: data.update_type,
            updatedAt: data.updated_at,
          })
          break

        default:
          console.warn('[SelfOrderChannel] Unknown message type:', data.type)
      }
    },

    /**
     * 通知音を再生
     * Web Audio APIベースのaudioPlayerを優先使用（iOS/iPadOS対応）
     */
    async playNotificationSound({ state }, soundType) {
      if (!state.soundEnabled) return

      // audioPlayerが設定されている場合は優先使用
      if (state.audioPlayer && state.audioPlayer.playSound) {
        try {
          const played = await state.audioPlayer.playSound(soundType)
          if (played) return
        } catch {
          // AudioPlayer再生失敗時はフォールバックへ
        }
      }

      // audioPlayerがない場合またはフォールバック
      try {
        const soundPath = NOTIFICATION_SOUNDS[soundType]
        if (soundPath) {
          const audio = new Audio(soundPath)
          audio.volume = 0.7
          await audio.play()
        }
      } catch {
        // フォールバック再生も失敗
      }
    },

    /**
     * バイブレーションを発動
     */
    triggerVibration({ state }) {
      if (!state.vibrationEnabled) return

      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200])
      }
    },

    /**
     * 未読カウントをクリア
     */
    clearUnreadCount({ commit }) {
      commit('CLEAR_UNREAD_COUNT')
    },

    /**
     * 通知音設定を更新
     */
    setSoundEnabled({ commit }, value) {
      commit('SET_SOUND_ENABLED', value)
    },

    /**
     * バイブレーション設定を更新
     */
    setVibrationEnabled({ commit }, value) {
      commit('SET_VIBRATION_ENABLED', value)
    },
  },
}
